import { ForwardedRef, forwardRef } from 'react';

import { DawnArrowDown, DawnArrowUp } from '@xemplo/icons';
import { useForwardedRef } from '@xemplo/ref-utils';

import { useNumberFieldMeta } from './hooks/use-number-field';
import { useNumberFormat } from './hooks/use-number-format';
import { useStepperEvents } from './hooks/use-stepper-events';
import * as S from './number-field.styles';
import { NumberFieldProps, StepperDirection } from './number-field.types';

/**
 * NumberField is a specialized input component that renders a number input field.
 * It extends the functionality of the XemploInput component, allowing for
 * additional properties specific to number fields.
 *
 * @param props - The properties for the NumberField component.
 * @returns A styled text input element with optional label, icons, and error handling.
 */
export const NumberField = forwardRef(
  (props: NumberFieldProps, forwardedRef?: ForwardedRef<HTMLInputElement>) => {
    const inputRef = useForwardedRef({ forwardedRef });
    const { inputProps, formatProps, stepperProps } = useNumberFieldMeta({
      ...props,
      inputRef,
    });

    const { handleBlur } = useNumberFormat(formatProps);
    const { arrowProps, handleKeyDown, handleWheel } = useStepperEvents(stepperProps);

    return (
      <S.CustomXemploInput
        {...inputProps}
        ref={inputRef}
        inputMode="decimal"
        onWheel={handleWheel}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        trailingIcon={
          <S.ArrowSteps>
            <S.ArrowContainer {...arrowProps(StepperDirection.Up)}>
              <DawnArrowUp />
            </S.ArrowContainer>
            <S.ArrowContainer {...arrowProps(StepperDirection.Down)}>
              <DawnArrowDown />
            </S.ArrowContainer>
          </S.ArrowSteps>
        }
      />
    );
  },
);
